/**
 * Vẽ bảng ngọc từ state core (Board) và diễn hoạt sự kiện (pop, booster, rơi, sinh mới).
 * Không gắn listener cho sprite: input hit-test theo lưới ở input.ts.
 */
import { Container, Graphics, Sprite, type Ticker } from 'pixi.js';
import { getCell, sameCoord, type Board, type Cell, type Coord } from '@ml/core';
import type { Config } from '@ml/config';
import type { Layout } from './layout.ts';
import { cellTint, orbContext, orbKey, overlayContext, overlayKey, textureFor } from './orbArt.ts';
import type { BoardEvent } from './boardEvents.ts';
import { easeInQuad, easeOutQuad, Tweener } from './tween.ts';

type AnimConfig = Config['ui']['anim'];

export class BoardView {
  readonly root = new Container();
  private readonly frame = new Graphics();
  private readonly grid = new Container();
  private readonly cellsLayer = new Container();
  private readonly fxLayer = new Graphics();
  private readonly highlight = new Graphics();
  private readonly line = new Graphics();
  private readonly mask = new Graphics();
  private sprites: (Container | null)[] = [];
  private board: Board;
  private layout: Layout | null = null;
  private path: readonly Coord[] = [];
  private candidates: readonly Coord[] = [];
  private pulse = 0;
  private readonly tweener: Tweener;
  animating = false;

  constructor(
    board: Board,
    ticker: Ticker,
    private readonly anim: AnimConfig,
    private readonly dimPct: number,
  ) {
    this.board = board;
    this.tweener = new Tweener(ticker);
    this.tweener.speed = Math.max(0.1, anim.speedPct / 100);
    this.grid.addChild(this.highlight, this.cellsLayer, this.line, this.fxLayer);
    this.grid.mask = this.mask;
    this.root.addChild(this.frame, this.grid, this.mask);
    ticker.add((t) => {
      if (this.path.length === 0) return;
      this.pulse = (this.pulse + t.deltaMS / 600) % 1;
      this.highlight.alpha = 0.65 + 0.35 * Math.sin(this.pulse * Math.PI * 2);
    });
  }

  get tweensActive(): number {
    return this.tweener.active;
  }

  /** Cần vẽ lại mỗi frame: đang có tween hoặc highlight đang nhịp. */
  get busy(): boolean {
    return this.tweener.active > 0 || this.path.length > 0;
  }

  setLayout(l: Layout): void {
    this.layout = l;
    const w = l.cell * this.board.width;
    const h = l.cell * this.board.height;
    this.frame
      .clear()
      .roundRect(l.board.x - 4, l.board.y, l.board.width + 8, l.board.height, 8)
      .fill(0x1c1c2c)
      .stroke({ width: 2, color: 0x34344e });
    this.frame.roundRect(l.gridX, l.gridY, w, h, 4).fill(0x14141f);
    this.grid.position.set(l.gridX, l.gridY);
    this.mask.clear().rect(l.gridX, l.gridY, w, h).fill(0xffffff);
    this.rebuild();
  }

  setBoard(board: Board): void {
    this.board = board;
    this.rebuild();
  }

  setSelection(path: readonly Coord[], candidates: readonly Coord[]): void {
    this.path = path;
    this.candidates = path.length === 0 ? [] : candidates;
    this.drawSelection();
  }

  private center(c: Coord): { x: number; y: number } {
    const cell = this.layout!.cell;
    return { x: (c.x + 0.5) * cell, y: (c.y + 0.5) * cell };
  }

  private makeSprite(cell: Cell): Container {
    const l = this.layout!;
    const box = new Container();
    const tex = textureFor(orbKey(cell));
    if (tex) {
      tex.source.scaleMode = 'nearest';
      const s = new Sprite(tex);
      s.anchor.set(0.5);
      box.addChild(s);
    } else {
      box.addChild(new Graphics(orbContext(cell)));
    }
    if (cell.t === 'orb' && cell.overlay) {
      const otex = textureFor(overlayKey(cell.overlay));
      if (otex) {
        otex.source.scaleMode = 'nearest';
        const o = new Sprite(otex);
        o.anchor.set(0.5);
        box.addChild(o);
      } else box.addChild(new Graphics(overlayContext(cell.overlay)));
    }
    box.scale.set(l.orbScale);
    return box;
  }

  private rebuild(): void {
    if (!this.layout) return;
    for (const s of this.sprites) s?.destroy({ children: true });
    this.cellsLayer.removeChildren();
    this.sprites = this.board.cells.map((cell, i) => {
      if (!cell) return null;
      const s = this.makeSprite(cell);
      const p = this.center({ x: i % this.board.width, y: Math.floor(i / this.board.width) });
      s.position.set(Math.round(p.x), Math.round(p.y));
      this.cellsLayer.addChild(s);
      return s;
    });
    this.drawSelection();
  }

  private drawSelection(): void {
    if (!this.layout) return;
    const l = this.layout;
    const active = this.path.length > 0;
    const dimAlpha = 1 - this.dimPct / 100;
    const s = l.orbScale;
    this.sprites.forEach((sp, i) => {
      if (!sp) return;
      const c = { x: i % this.board.width, y: Math.floor(i / this.board.width) };
      const inPath = this.path.some((p) => sameCoord(p, c));
      const cand = this.candidates.some((p) => sameCoord(p, c));
      sp.alpha = !active || inPath || cand ? 1 : dimAlpha;
      const head = active && sameCoord(this.path[0]!, c);
      sp.scale.set(head ? s * 1.15 : s);
    });

    this.highlight.clear();
    this.line.clear();
    if (!active) return;
    const cell = l.cell;
    const tail = this.path[this.path.length - 1]!;
    for (const c of this.candidates) {
      this.highlight
        .roundRect(c.x * cell + 2, c.y * cell + 2, cell - 4, cell - 4, 6)
        .stroke({ width: 3, color: 0xfff2a0 });
      const far = Math.max(Math.abs(c.x - tail.x), Math.abs(c.y - tail.y)) > 1;
      if (far) {
        // Chấm chỉ hướng cho ứng viên tầm xa (core-loop 5.3).
        const a = this.center(tail);
        const b = this.center(c);
        this.highlight.circle((a.x + b.x) / 2, (a.y + b.y) / 2, 3).fill(0xfff2a0);
      }
    }
    for (const c of this.path) {
      this.highlight
        .roundRect(c.x * cell + 2, c.y * cell + 2, cell - 4, cell - 4, 6)
        .fill({ color: 0xffffff, alpha: 0.18 });
    }
    const tint = cellTint(
      this.path.map((p) => getCell(this.board, p.x, p.y)).find((c) => c?.t === 'orb') ?? null,
    );
    const first = this.center(this.path[0]!);
    this.line.moveTo(first.x, first.y);
    for (const p of this.path.slice(1)) {
      const q = this.center(p);
      this.line.lineTo(q.x, q.y);
    }
    if (this.path.length > 1) {
      this.line.stroke({
        width: Math.max(4, cell * 0.12),
        color: tint,
        alpha: 0.9,
        cap: 'round',
        join: 'round',
      });
    }
    for (const p of this.path) {
      const q = this.center(p);
      this.line.circle(q.x, q.y, Math.max(3, cell * 0.08)).fill(0xffffff);
    }
  }

  /** Diễn sự kiện từ bảng cũ sang `next`. Trả về khi anim xong. */
  async play(next: Board, events: readonly BoardEvent[]): Promise<void> {
    if (!this.layout) {
      this.board = next;
      return;
    }
    this.animating = true;
    this.setSelection([], []);
    const cell = this.layout.cell;
    const w = this.board.width;
    const jobs: Promise<void>[] = [];

    for (const ev of events) {
      if (ev.type === 'pop') {
        for (const c of ev.cells) {
          const sp = this.sprites[c.y * w + c.x];
          if (!sp) continue;
          const s0 = sp.scale.x;
          jobs.push(
            this.tweener.to(this.anim.popMs, (p) => {
              sp.scale.set(s0 * (1 + 0.3 * p) * (1 - easeInQuad(p)));
              sp.alpha = 1 - p;
            }),
          );
        }
      } else if (ev.type === 'booster') {
        const fx = new Graphics();
        for (const a of ev.area) fx.rect(a.x * cell, a.y * cell, cell, cell);
        fx.fill({ color: ev.color ?? 0xfff4c0, alpha: 0.85 });
        this.fxLayer.parent?.addChild(fx);
        jobs.push(
          this.tweener.to(this.anim.boosterMs, (p) => (fx.alpha = 1 - p)).then(() => fx.destroy()),
        );
      }
    }
    await Promise.all(jobs);

    const shuffled = events.some((e) => e.type === 'shuffle');
    this.board = next;
    this.rebuild();

    if (shuffled) {
      await this.tweener.to(this.anim.popMs * 2, (p) => {
        for (const sp of this.sprites) if (sp) sp.alpha = p;
      });
    } else {
      const start = new Map<number, number>(); // index đích → y bắt đầu (đơn vị ô)
      for (const ev of events) {
        if (ev.type === 'fall') for (const m of ev.moves) start.set(m.to.y * w + m.to.x, m.from.y);
        if (ev.type === 'spawn') {
          const perCol = new Map<number, number>();
          for (const s of ev.cells) perCol.set(s.x, (perCol.get(s.x) ?? 0) + 1);
          for (const s of ev.cells) start.set(s.y * w + s.x, s.y - perCol.get(s.x)!);
        }
      }
      const falls: Promise<void>[] = [];
      for (const [i, fromY] of start) {
        const sp = this.sprites[i];
        if (!sp) continue;
        const toY = Math.floor(i / w);
        const dist = toY - fromY;
        if (dist <= 0) continue;
        const y1 = sp.y;
        const y0 = y1 - dist * cell;
        falls.push(
          this.tweener.to(this.anim.fallMsPerCell * dist, (p) => {
            sp.y = Math.round(y0 + (y1 - y0) * easeOutQuad(p));
          }),
        );
      }
      await Promise.all(falls);
    }
    this.animating = false;
  }
}
